import { Link, matchPath, useLocation } from "react-router";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  LayoutDashboard,
  Activity,
  Users,
  Building2,
  Handshake,
  Megaphone,
  ListOrdered,
  FileText,
  Mail,
  TrendingUp,
  Radar,
  Bot,
  Cpu,
  Settings2,
  Zap,
  Lightbulb,
  ListChecks,
  List,
  ScrollText,
  Gauge,
  Filter,
  Search,
  DatabaseZap,
  Columns3,
  Workflow,
  GitBranch,
  BarChart3,
  ShieldCheck,
  FileBarChart,
  Settings,
  Plug,
  AtSign,
  Scale,
  UserCog,
  FolderKanban,
  MessageSquare,
  History,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Network,
  Boxes,
  UploadCloud,
  Wrench,
  CalendarDays,
  Coins,
  Download,
  Presentation,
  Package,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useAttentionCount, isQuiet, type AttentionCount } from "@/hooks/useAttentionCounts";
import { NeedsYouBadge } from "@/components/ui/NeedsYouBadge";
import { useAdminAccess, useCurrentEntityId } from "@/providers/firebase/authProvider";

type NavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  badge?: string;
  adminOnly?: boolean;
};

type NavCategory = {
  label: string;
  defaultOpen?: boolean;
  highlight?: boolean;
  adminOnly?: boolean;
  items: NavItem[];
};

const LANDING_PAGE_ITEM: NavItem = {
  label: "Landing Page",
  to: "/",
  icon: LayoutDashboard,
};

const NAV_CATEGORIES: NavCategory[] = [
  {
    label: "HARNESS",
    defaultOpen: true,
    highlight: true,
    adminOnly: true,
    items: [
      { label: "Home", to: "/harness", icon: Radar, badge: "HAR" },
      { label: "Audit", to: "/harness/audit", icon: ScrollText, badge: "HAR" },
      { label: "Pacing", to: "/harness/pacing", icon: Gauge, badge: "HAR" },
      // THE MEASUREMENT ROW LEFT THIS GROUP ON 2026-10-02, and the reasoning is the same one that
      // moved `Entity Review` out of it (P144 D-144-015). It sat in the ADMIN-ONLY set, so in user
      // mode THE CUSTOMER HAD NO SCREEN FOR HIS OWN NUMBERS AT ALL — the pillar surface was built,
      // deployed and invisible, which is `@P136` §9.2's finding for the product as a whole.
      // `D-136-005` already rules it: *"the customer view must show the pillars"*. So the row now
      // lives with the owner's other surfaces (see MY WORKSPACE below) and carries no `HAR` badge —
      // that badge MEANS "restricted area" in this component, and a measurement is the owner's.
      // The room is where the operator TALKS to the company rather than reading a report
      // about it — the other three tabs are views of state, this one is the conversation.
      { label: "Room", to: "/harness/room", icon: MessageSquare, badge: "HAR" },
    ],
  },
  {
    label: "MY WORKSPACE",
    defaultOpen: true,
    items: [
      // PROJECT-287 IN-287-25 — THE DOCUMENTS ROW (the PO, 2026-10-09): *"in the sidebar we will see
      // 'My Documents' somewhere in my workspace"*. It sits FIRST in this group because it is the
      // surface a founder opens most, and it carries no `HAR` badge: that badge MEANS "restricted
      // area" in this component, and his own documents are not restricted. Until now the documents
      // surface was registered on both shells and reachable only by typing an address — the exact
      // shape `check_page_route_reachability.mjs` R3 exists to catch.
      { label: "My Documents", to: "/documents", icon: FileText },
      { label: "My Entities", to: "/entities", icon: Building2 },
      // P280 T0201/T0203 — THE PITCH PREPARATION ROW. It is the way IN for the person the
      // surface was built for, and it exists because the page would otherwise be the exact
      // defect `check_page_route_reachability.mjs` R3 was added for: a page mounted, imported,
      // registered and green under R1 and R2, and that NO USER CAN OPEN. It sits in MY
      // WORKSPACE and carries no `HAR` badge — that badge means "restricted area" in this
      // component, and her own fundraise preparation is hers. The gates INSIDE this page are
      // what check the PO's own requirement (*"THE ENTITY MUST EXIST. EXTENSIVE ENTITY URL
      // scrape must exist"*), so the row has to be reachable for those gates to speak at all.
      { label: "Fundraise prep", to: "/pitch/prepare", icon: Presentation },
      // T0501 — what we PROVIDE, beside what we check. A founder shopping for a fundraise reads
      // this; the price of the one hand-built row is on it, and the Subscriber is its own gate.
      { label: "Fundraise outcomes", to: "/pitch/deliverables", icon: Package },
      // P218 T218-011 — THE DOOR, as a ROW as well as a link. A door a visitor can only reach once
      // (the moment after his five land) is not a door: the company he was given stays bare for
      // ever unless he can find this again. It is the owner's own workspace and carries no `HAR`
      // tag, and it is also where @P160's console lives — until now that console was imported by
      // nothing and on no route, so NO user could start a company setup from the interface at all.
      { label: "Company setup", to: "/entity-setup", icon: Wrench },
      // THE REVIEW SURFACE LIVES HERE, NOT IN `HARNESS` (P144 D-144-015, 2026-09-27). It was a row
      // inside the admin-only HARNESS group, which meant the red number on the pill announced work
      // and the sidebar had no row for it — measured in user mode: `pillEntity: 99`,
      // `sidebarBadge: null`. It is the owner's queue, so it sits with the owner's workspace, and
      // it carries no `HAR` tag: that badge MEANS "restricted area" in this component (see its own
      // render branch), and a restricted-looking row on an open surface teaches the wrong thing.
      // ⚠️ THE ADDRESS FOLLOWED THE ROW ON 2026-10-05 (P042 `D-042-016`): `/entity-review`, not
      // `/harness/entity-review`. The PO's ruling is that `/harness/*` is the operator's area and
      // holds harness surfaces ONLY — a surface its owner reaches in user mode must leave the
      // namespace, and the old path stays alive as a redirect.
      { label: "Entity Review", to: "/entity-review", icon: ListChecks },
      // THE NUMBERS HE STEERS BY — MOVED HERE 2026-10-02 (P042 `D-042-014`; the PO: *"how would it
      // look like?"* · *"we have a View CEO/CTO/CMO or what? … where to put it?"*). `D-042-014` is
      // the decision that IS this move; `D-042-004` is its neighbour, the one that settled the
      // numbers are a view and never a landing — this commit's message names `D-042-005`, which is
      // the BD80 definition decision and belongs to neither. It sits beside
      // `Entity Review` because it is the same kind of row: the OWNER's own surface, needing no
      // admin rights, carrying no `HAR` tag. `@P229` `IN-10` settles why it is not a seat view —
      // **CEO · CTO · CMO are `party (internal)` = OUR seats**; a customer's seats are COO and CFO,
      // and his own numbers are simply his own. And `@P229` design rule 1 settles why it is a row
      // and not a page he lands on: *"one surface, one altitude at a time — the portfolio is a
      // count, the decision is one row."* The label stays `KPIs`, the manifest's own word (MR13):
      // calling it "State & KPIs" would collide with Home, which is where the room's STATE lives.
      // The path reads `/kpis` (P042 `D-042-016`, PO 2026-10-05): the row left the admin-only group
      // on 2026-10-02, and the ADDRESS left with it — `/harness/*` is the operator's area, and the
      // owner's own numbers are not part of it. The former `/harness/kpis` stays alive as a redirect,
      // and the route is no longer `AdminOnlyRoute`-gated: while it was, the visible row sent a
      // non-admin to `/outreach`, which is the symptom `D-042-014` set out to remove.
      { label: "KPIs", to: "/kpis", icon: BarChart3 },
      // MOVED 2026-09-21 (👤): the integration vault left its own "1 Integrations" pillar
      // group and sits here, with the workspace's other real surfaces. It is the one
      // admin-and-user surface that belongs to "my workspace" rather than to a pillar,
      // which is why it is a ROW here and not a group of its own.
      // NEW BADGE STRUCK 2026-09-21 (👤): one of the three USER-REACHABLE rows that
      // carried it (with Outreach and CRM). The rows behind admin mode keep theirs,
      // and the Meetings/Contracts "SOON" placeholders are untouched — so the render
      // branch for "NEW" at ~:451 is still live and must not be removed.
      { label: "Integrations", to: "/integrations", icon: Plug },
      { label: "Activity Feed", to: "/activity-feed", icon: Activity, adminOnly: true },
    ],
  },
  // ── The CRM group was DISSOLVED 2026-09-21 (👤), not renumbered. ────────────────────
  //
  // It held two tabs — Contacts (`/contacts`, person entities) and Companies
  // (`/companies`, company entities) — over the same `entities` table. The PO asked for
  // ONE tab that is "basically a combination of contacts and companies", so the two
  // became a single row: `Contacts` → `/crm`, carrying the EXTERNAL half of the entity
  // graph (see `CrmPage.tsx` for the definition of external).
  //
  // ⚠️ THE TWO ROUTES WERE NOT DELETED. `/contacts` and `/companies` still exist and
  // still render (`ContactsPage`/`CompaniesPage` → `EntityListPage`); only their NAV
  // ENTRIES are gone, because after the combination they would have been the same list
  // three times over. They remain the rollback if the PO wants the split tabs back.
  {
    // RENAMED 2026-09-15 (👤): "OUTREACH" → "2 Com Hub".
    //
    // DE-NUMBERED 2026-09-21 (👤): "2 Com Hub" → "Com Hub". The PO struck the pillar
    // numbers from the sidebar in the same pass that dissolved "1 Integrations" — see
    // the note on Meetings below for the one group that still carries a number.
    //
    // The group's real subject was never outreach — it is the COMMUNICATION HUB: the email
    // surface itself (`/email`, P133), the channels that feed it (Campaigns, Sequences,
    // Templates) and the one outreach journey built on it. "Outreach" named the *activity* and
    // hid the channel set, which is the same naming failure P136 records for other labels.
    //
    // ⚠️ The NAME IS TITLE CASE IN SOURCE AND UPPERCASE ON SCREEN, and that is deliberate: the
    // group heading carries CSS `uppercase` (see the className at ~:306), so every group
    // renders the same way while the SOURCE keeps P136's canonical spelling — "Com Hub", the
    // pillar's own name (`project136_operation_clarification.md`: pillar_2_comhub). Never
    // "fix" the case here to match the pixels.
    label: "Com Hub",
    defaultOpen: true,
    items: [
      // ORDER 2026-09-21 (👤): Investor Outreach first, Email last. The journeys come
      // before the inbox: the outreach row is where work is CREATED, `/email` is where
      // every channel's prepared items are then reviewed.
        // `Investor Outreach` became **Outreach** on 2026-09-21 (P080 D-125, PO): the row is the
        // DOMAIN CONTAINER. Equity Raise is the live domain inside it (the switcher on the page) and
        // Procurement/Recruitment join it, so the row is named after the job rather than after one of
        // the domains it will hold — the same move as `Email` → `Messages`.
        // NEW BADGE STRUCK 2026-09-21 (👤) — one of the three user-reachable rows
        // (with Integrations and CRM); see the Integrations row for the full note.
        { label: "Outreach", to: "/outreach", icon: TrendingUp },
      // The CRM surface (see the dissolved-group note above). It sits in Com Hub because
      // a contact list is "who I talk to", and Com Hub is the pillar that owns talking.
      // ── THE ROW READS `CRM`, DECIDED 2026-09-21 (PO IN-03; gate verdict in @P013 D-008) ──
      // His list wrote `CRM - [Contacts](/crm)`; the first pass named the row after the
      // OBJECT, which under-announces a page that holds companies too, and he flagged the
      // missing word. One row = one name: the category, not half its contents.
      // NEW BADGE STRUCK 2026-09-21 (👤) — one of the three user-reachable rows
      // (with Integrations and Outreach); see the Integrations row for the full note.
      { label: "CRM", to: "/crm", icon: Users },
        // `Email` became **Messages** on 2026-09-21 (P080 D-125, PO): the container is named after
        // the CHANNELS it holds, not after one of them — WhatsApp and LinkedIn sit inside it.
        { label: "Messages", to: "/messages", icon: Mail },
        // ⚠️ MOVED HERE 2026-10-09 FROM `SETTINGS` (which is `adminOnly`), because the page it opens
        // is built FOR THE FOUNDER and she could not reach it. MEASURED: the readiness panel is the
        // programme's trust surface (D-003/D-014 — *"we create trust through controlled execution"*),
        // the page itself carries NO admin guard, and the backend route
        // (`/api/v1/warmup/readiness`) needs only a valid user token — but the ONLY way in was a
        // sidebar section gated on `adminOnly: true`, and `useAdminAccess()` demands BOTH the `admin`
        // claim AND admin MODE, so a founder saw no entry at all and the drawer was unreachable.
        // It belongs in Com Hub for the same reason `Messages` does: it is about the addresses we
        // send from. ⚠️ AND IT DOES NOT VIOLATE THE ADMIN-HIDING RULE: `tests/e2e/probe_p13_user_rail.mjs`
        // names exactly what counts as admin machinery in user mode, and "Sending addresses" is not
        // among them.
        { label: "Sending addresses", to: "/sending-accounts", icon: AtSign, badge: "NEW" },
      { label: "Campaigns", to: "/campaigns", icon: Megaphone, adminOnly: true },
      { label: "Sequences", to: "/sequences", icon: ListOrdered, badge: "NEW", adminOnly: true },
      { label: "Templates", to: "/templates", icon: FileText, adminOnly: true },
    ],
  },
  {
    // project139 placeholder — Pillar 3 Meetings surface (sidebar IA only until the pillar
    // build executes). The route renders an honest empty state so the four-pillar frame is
    // visible and truthful.
    //
    // DE-NUMBERED 2026-09-21 (👤): "3 Meetings" → "Meetings", and in the same pass "4 Contracts"
    // → "Contracts" — his word for the second one: *"oops. remove. yes. consistency"*. So NO
    // group in this rail carries a pillar number any more (and "1 Integrations" no longer exists
    // as a group at all — see the MY WORKSPACE note).
    label: "Meetings",
    defaultOpen: true,
    items: [
      { label: "Meetings", to: "/meetings", icon: CalendarDays, badge: "SOON" },
    ],
  },
  {
    // project136 / project140 — Pillar 4 Contracts (the only MAXIMISED pillar). Deals and
    // the pipeline board are the contract/value surfaces today; they move out of CRM/PIPELINES
    // so the pillar is visible as the terminus of the chain.
    //
    // ⚠️ DE-NUMBERED 2026-09-21 (👤), completing the pass that dissolved "1 Integrations" and
    // struck the numbers from "2 Com Hub"/"3 Meetings". SCOPE, because this is easy to over-read:
    // the NAV GROUP stopped showing the number; the PILLAR still carries it everywhere the pillar
    // itself is named (project files, decks, KPI namespaces, `pillar_4_contract`). `MR33`
    // ("THE NUMBER IS PART OF THE NAME") still governs the pillar and is now superseded FOR THE
    // RAIL — recorded in @P136 `IN-74`, deliberately not edited into the manifest by an agent.
    label: "Contracts",
    defaultOpen: true,
    items: [
      // The fourth link, made VISIBLE 2026-09-21 (👤): both rows below are admin-gated, so the
      // group emptied out and the pillar vanished from user mode — which reads as a chain of
      // three. This row is the honest placeholder (`ContractsPage`, @P136 §16.5's shape: one
      // state + one real action), and it is what makes the group render for a user at all.
      { label: "Contracts", to: "/contracts", icon: Handshake, badge: "SOON" },
      { label: "Deals", to: "/deals", icon: Handshake, adminOnly: true },
      { label: "Pipeline Board", to: "/pipeline-board", icon: Columns3, badge: "NEW", adminOnly: true },
    ],
  },
  {
    // ADMIN-ONLY 2026-09-21 (👤): "AI AGENTS (hence this should not be visible in user
    // mode)" — each of the three rows was marked admin-only individually, and the group
    // goes with them so it cannot render as an empty shell.
    label: "AI AGENTS",
    defaultOpen: false,
    highlight: true,
    adminOnly: true,
    items: [
      { label: "Agent Hub", to: "/agent-hub", icon: Bot, badge: "AI" },
      { label: "Skill Catalog", to: "/skills", icon: Boxes, badge: "AI" },
      { label: "Process Manager", to: "/process-manager", icon: Network, badge: "AI" },
      { label: "Agent Activity", to: "/agent-activity", icon: Zap, badge: "AI", adminOnly: true },
    ],
  },
  {
    label: "ADMIN",
    defaultOpen: true,
    adminOnly: true,
    items: [
      { label: "Admin - Bot / Kernels", to: "/agent-config", icon: Cpu },
      { label: "Development Requests", to: "/development-requests", icon: Wrench },
      // P098 T0422 — the import door, and the other half of the same job: this one brings a skill
      // IN, that one tracks a skill we could not find. Both are admin-only, and so is this page's
      // SERVER gate (`validate_admin_token` on every /skill-import route) — `adminOnly` here only
      // hides the nav row, which is presentation and never access control.
      { label: "Skill Import", to: "/skill-import", icon: Download },
      // PROJECT-111A T0844 — the review queue's INBOX. The route existed and the page
      // worked, but nothing linked to it: `KernelFixRunPanel` is mounted inside
      // `MarkableOutput` and needs a `patchRunId`, so navigating away from that output
      // left every background run unreachable from the UI. Listed here, immediately
      // before Proposals, because that is the order the workflow runs in — a run
      // produces the proposals that page then rules on.
      { label: "Kernel Runs", to: "/kernel-runs", icon: GitBranch, badge: "AI" },
      { label: "Proposals", to: "/proposals", icon: Lightbulb, badge: "AI" },
      // @P091 T0112 (D-103) — an operator's view of ONE customer's credits: the balance, the plan,
      // what an email costs him, every movement that produced the balance, and an adjustment with a
      // reason. Last in the ADMIN group because it is the only row here that touches a customer's
      // money directly, and it should not sit between "Bot / Kernels" and "Proposals".
      { label: "Customer Credits", to: "/customer-credits", icon: Coins },
    ],
  },
  {
    label: "INTELLIGENCE",
    defaultOpen: true,
    highlight: true,
    adminOnly: true,
    items: [
      { label: "Upload Anything", to: "/upload-anything", icon: UploadCloud, badge: "NEW" },
    ],
  },
  {
    label: "DATA",
    defaultOpen: false,
    adminOnly: true,
    items: [
      { label: "Lists", to: "/lists", icon: List },
      { label: "Segments", to: "/segments", icon: Filter },
      { label: "Prospecting", to: "/prospecting", icon: Search, badge: "NEW" },
      { label: "Enrichment", to: "/enrichment", icon: DatabaseZap, badge: "NEW" },
    ],
  },
  {
    label: "AUTOMATION",
    defaultOpen: false,
    adminOnly: true,
    items: [
      { label: "Flow Builder", to: "/automation", icon: Workflow },
      { label: "Workflows", to: "/workflows", icon: GitBranch },
    ],
  },
  {
    label: "ANALYTICS",
    defaultOpen: false,
    adminOnly: true,
    items: [
      { label: "Dashboard", to: "/analytics", icon: BarChart3 },
      { label: "Deliverability", to: "/deliverability", icon: ShieldCheck, badge: "NEW" },
      { label: "Reports", to: "/campaign-reports", icon: FileBarChart, badge: "NEW" },
    ],
  },
  {
    label: "SETTINGS",
    defaultOpen: false,
    adminOnly: true,
    items: [
      { label: "Admin Settings", to: "/admin/settings", icon: Settings },
      { label: "Compliance", to: "/compliance", icon: Scale, badge: "NEW" },
      { label: "Team", to: "/team", icon: UserCog, badge: "NEW" },
      { label: "Projects", to: "/projects", icon: FolderKanban, badge: "NEW" },
    ],
  },
  {
    label: "FEEDBACK",
    defaultOpen: false,
    adminOnly: true,
    items: [
      { label: "Review Queue", to: "/feedback-queue", icon: MessageSquare, badge: "NEW" },
      { label: "History", to: "/feedback-history", icon: History, badge: "NEW" },
    ],
  },
  {
    // ADMIN-ONLY 2026-09-21 (👤): this group and "1 Integrations" both listed `/connections`,
    // so the same page appeared twice in USER mode — the duplicate the PO asked about. It
    // survives here, in the group that means it, and only in admin mode.
    //
    // WHAT `/connections` IS (so the name can be judged): the viewer-to-viewer graph of
    // people on Raisy — recommendations scored from shared entities, plus incoming
    // connection requests (`/api/v1/connections/*`). It has NOTHING to do with the CRM's
    // Contacts: those are external people in `entities`, these are other Raisy users.
    // ⚠️ NAMING OPEN — the PO asked for an unambiguous name or removal; not guessed here.
    label: "NETWORK",
    defaultOpen: false,
    adminOnly: true,
    items: [
      { label: "Connections", to: "/connections", icon: Network },
    ],
  },
];

/**
 * The dot's own words (P144 D-144-011): a number that cannot be named in one word is the failure
 * this project exists to remove, so the count carries WHAT it holds — a prepared draft is not an
 * anonymous unit. Deliberately the same wording as the entity switcher's badge.
 */
function badgeNounFor(c: AttentionCount | undefined): string {
  if (!c || c.preparation <= 0) return "need you";
  const prepared = c.preparation === 1 ? "1 prepared draft" : `${c.preparation} prepared drafts`;
  return `need you · ${prepared}`;
}

export function CrmSidebar() {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const sidebar = useSidebar();
  // Both nav dots read the ONE attention store (P144 D-144-001/011/012) — the same shared query
  // the entity switcher reads, so these rows add READERS and not additional pollers (the
  // three-pollers defect this project removed, IN-01).
  // The Email row shows PREPARATIONS — drafts the machine wrote and is waiting on a decision for.
  // The Process Manager row shows the entity's PROCESS GATE. A zero renders nothing, and so does
  // "not heard yet": both are silence, never a fabricated count.
  const currentEntityId = useCurrentEntityId();
  const attention = useAttentionCount(currentEntityId);
  const preparedDrafts = attention?.preparation ?? 0;
  const pendingReviewCount = attention?.processGate ?? 0;
  // ── THE QUIET STATE (P144 D-144-019, 2026-09-28) ──────────────────────────────────────────
  // "you have opened every item this number is made of" → 50% intensity, same number, and it
  // never clears on a view (D-144-009). Derived only where a view is a recorded fact:
  //   · the Entity Review row carries the entity's WHOLE count, so it may go quiet only if that
  //     whole count IS process reviews — `isQuiet` takes the with-facts figure for exactly this;
  //   · the Process Manager row's number IS the process gate, so all of it has the fact;
  //   · the Email row counts DRAFTS, and since 2026-09-28 those have a view fact of their own
  //     (`draft_views`, migration 000032, written when a draft is opened). `preparationViewFacts`
  //     says whether that read could be made at all — on an environment without the table it is
  //     false, the with-facts figure is 0, and the row stays FULL RED. That flag, not the zero,
  //     is what decides: a count may never dim on facts it does not have.
  // T0404 — the review surface's row carries the entity's WHOLE outstanding count
  // (questions + preparations, D-144-011), because that row is where every one of them
  // is resolved. The other rows carry their own LANE, which is a SUBSET of this number:
  // that is why the roll-up below takes the rows' maximum and never their sum — adding
  // them would count one pending draft twice and invent a number nobody can act on.
  // DECLARED BEFORE ITS READERS, WITH THE OTHER COUNTS (2026-09-28). It used to sit BELOW the two
  // `isQuiet` calls that read it, beside the paragraph above, and that killed the whole shell:
  // the minified bundle threw `Cannot access 'p' before initialization` in this component, and
  // `Layout` renders this rail, so EVERY page went down and not just the navigation. A count
  // belongs with the counts; the roll-up paragraph moved up with it so its reason still sits on
  // the line it explains. `tsc` names this class (TS2448) — it ships because the DEV FE build
  // does not run it.
  const attentionTotal = attention?.total ?? 0;
  const reviewRowQuiet = isQuiet(attentionTotal, attention?.processGateViewed, attention?.processGate);
  const gateRowQuiet = isQuiet(pendingReviewCount, attention?.processGateViewed, attention?.processGate);
  // The drafts row's own facts: when the view read could not be made, its with-facts figure is 0 and
  // the guard refuses to dim — the number the reader sees is then exactly the number that was
  // measured, and none of it is vouched for.
  const draftsRowQuiet = isQuiet(
    preparedDrafts,
    attention?.preparationViewed,
    attention?.preparationViewFacts ? preparedDrafts : 0,
  );
  /** What a single nav row shows, or 0. One place decides, so the roll-up cannot drift. */
  const itemAttention = (to: string): number => {
    if (to === "/entity-review") return attentionTotal;
    if (to === "/process-manager") return pendingReviewCount;
    if (to === "/messages") return preparedDrafts;
    return 0;
  };
  /** The roll-up for a collapsed group: the largest need it holds, never the sum of subsets. */
  const categoryRollup = (items: NavItem[]): number =>
    items.reduce((max, item) => Math.max(max, itemAttention(item.to)), 0);
  const showAdmin = useAdminAccess();
  const { openMobile, setOpenMobile, toggleSidebar } = sidebar;
  const handleClick = () => {
    if (openMobile) setOpenMobile(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return !!matchPath("/", location.pathname);
    return !!matchPath(`${to}/*`, location.pathname);
  };

  const visibleCategories = NAV_CATEGORIES
    .filter((cat) => showAdmin || !cat.adminOnly)
    .map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => showAdmin || !item.adminOnly),
    }))
    .filter((cat) => cat.items.length > 0);

  return (
    <Sidebar variant="floating" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <Link to="/" className="flex items-center gap-2">
                {darkModeLogo ? (
                  <img
                    className="[.light_&]:hidden h-5 w-5 object-contain"
                    src={darkModeLogo}
                    alt={title}
                  />
                ) : null}
                {lightModeLogo ? (
                  <img
                    className="[.dark_&]:hidden h-5 w-5 object-contain"
                    src={lightModeLogo}
                    alt={title}
                  />
                ) : null}
                <span className="text-base font-semibold">{title}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {visibleCategories.map((cat) => (
          <Collapsible
            key={cat.label}
            defaultOpen={cat.defaultOpen ?? false}
            className="group/collapsible"
          >
            <SidebarGroup className="py-0">
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger
                  data-testid={`nav-category-${cat.label}`}
                  className={`flex w-full items-center text-xs font-semibold uppercase tracking-wider ${
                    cat.highlight
                      ? "text-[#0AB3BF]"
                      : "text-muted-foreground"
                  }`}
                >
                  {cat.label}
                  {/* T0404 — THE HIDDEN-BADGE DEFECT (IN-01): with this group collapsed, a
                      correct number sat inside a closed group and the user saw nothing. The
                      roll-up rides on the header and is hidden again the moment the group is
                      open, where the rows themselves carry their counts. `max` of the rows is
                      the honest figure: the rows hold overlapping LANES of one set. */}
                  {/* NO QUIET STATE ON THE ROLL-UP, and the reason is a fact, not a preference: its
                      figure is the MAX of the rows' overlapping lanes, so the number on screen may
                      belong to a lane that has view facts or to one that has none (drafts). A figure
                      that cannot name which items it is made of cannot honestly say "you opened all
                      of these" — so it stays full red until it can. */}
                  <NeedsYouBadge
                    count={categoryRollup(cat.items)}
                    noun={`in ${cat.label.toLowerCase()}`}
                    className="ml-1.5 group-data-[state=open]/collapsible:hidden"
                  />
                  <ChevronRight className="ml-auto h-3.5 w-3.5 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {cat.items.map((item) => (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive(item.to)}
                          tooltip={item.label}
                        >
                          <Link
                            to={item.to}
                            onClick={handleClick}
                            style={
                              cat.highlight && isActive(item.to)
                                ? { color: "#0AB3BF" }
                                : undefined
                            }
                          >
                            <item.icon className="h-4 w-4" />
                            <span className="flex-1">{item.label}</span>
                            {/* The review surface's row carries the entity's whole outstanding
                                count — the number the top-bar dot shows, in the place the user
                                looks for it (T0404). */}
                            {item.to === "/entity-review" && attentionTotal > 0 && (
                              <NeedsYouBadge
                                count={attentionTotal}
                                noun={badgeNounFor(attention)}
                                dimmed={reviewRowQuiet}
                              />
                            )}
                            {/* The Email row follows the red dot too (P133 T1307): the drafts the
                                machine prepared for this company are a PREPARATION, and this is
                                the ONE count that carries them. Byte-identical component to the
                                entity switcher's — one look, one meaning, everywhere. */}
                            {item.to === "/messages" && preparedDrafts > 0 && (
                              <NeedsYouBadge
                                count={preparedDrafts}
                                noun={preparedDrafts === 1 ? "prepared draft" : "prepared drafts"}
                                dimmed={draftsRowQuiet}
                              />
                            )}
                            {item.badge && (
                              <span
                                className="text-label-2xs px-1.5 py-0.5 rounded-full font-semibold"
                                style={
                                  item.badge === "NEW"
                                    ? { background: "rgba(245,158,11,0.16)", color: "#F59E0B" }
                                    : item.badge === "HAR"
                                    // HAR = the Harness surface, which is ADMIN-ONLY
                                    // (PO 2026-09-16). Red on purpose: it is the one
                                    // badge that flags a restricted area, so it must not
                                    // read like the informational NEW/AI tags.
                                    ? { background: "rgba(220,38,38,0.16)", color: "#DC2626" }
                                    : item.badge === "SOON"
                                    // SOON = a pillar/frame placeholder whose surface is not
                                    // built yet (project139 Meetings). Neutral so it does not
                                    // look like a live feature.
                                    ? { background: "rgba(100,116,139,0.14)", color: "#64748B" }
                                    : { background: "rgba(10,179,191,0.14)", color: "#0AB3BF" }
                                }
                              >
                                {item.badge}
                              </span>
                            )}
                            {item.to === "/process-manager" && pendingReviewCount > 0 && (
                              // MERGED onto the store 2026-09-21 (D-144-012), which CHANGED what
                              // this number means: it used to come from `/process/reviews/pending`,
                              // an endpoint that ignores its `entity_id` argument and counts every
                              // pending review the caller can reach — cross-entity, and including
                              // rows with no `processCallId`. On a sidebar that is entity-scoped by
                              // design that number could not be explained in a word. It is now the
                              // ACTIVE entity's process gate, from the same query as the row above.
                              // Rendered by the ONE shared badge (T0400): it was an inline red pill
                              // with its own markup, which is exactly how two dots drift apart.
                              // `usePendingReviewCount` was retired at the same time (T0404) — its
                              // endpoint ignored the entity it was handed, so its number could not
                              // be named in a word (D-144-012).
                              <NeedsYouBadge
                                count={pendingReviewCount}
                                noun={pendingReviewCount === 1 ? "item to review" : "items to review"}
                                dimmed={gateRowQuiet}
                              />
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isActive(LANDING_PAGE_ITEM.to)}
              tooltip={LANDING_PAGE_ITEM.label}
            >
              <Link to={LANDING_PAGE_ITEM.to} onClick={handleClick}>
                <LANDING_PAGE_ITEM.icon className="h-4 w-4" />
                <span className="flex-1">{LANDING_PAGE_ITEM.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip={sidebar.state === "expanded" ? "Collapse sidebar" : "Expand sidebar"}
              onClick={toggleSidebar}
              className="cursor-pointer"
            >
              {sidebar.state === "expanded" ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeftOpen className="h-4 w-4" />
              )}
              <span>Collapse</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
